import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { EStavLeiPlatby, ILeiData, IZalozeniLeiInput } from 'types/leiTypes';

import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';

import useProfily from 'hooks/useProfily';

import leiDataApi from 'services/lei/novyLeiData';

import { ETypMatky, ETypZadostiLei } from 'enums/lei/leiDataEnums';
import { ELeiStavZadosti } from 'enums/lei/leiStavZadosti';

import getLanguageObject from 'utils/getLanguageObject';

import DetailInfo from './DetailInfo';
import Osoba from './Osoba';
import Prehled from './Prehled';
import PrimaMatka from './PrimaMatka';
import Profondy from './Profondy';
import Pruvodce from './Pruvodce';
import RozcestnikZalozeni from './RozcestnikZalozeni';

interface INovyLei {}

const NovyLei: React.FC<INovyLei> = () => {
    const { setLevel } = useProfily();
    const { language, lei, cisStaty, platbaLei } = useContext(Context);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [nextStepQueued, setNextStepQueued] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [showTitle, setShowTitle] = useState<boolean>(true);
    const [formData, setFormData] = useState<ILeiData | undefined>();
    const [pruvodceData, setPruvodceData] = useState<IZalozeniLeiInput>();
    const [PrehledOnly] = useState<boolean>(
        lei?.stavZadosti === ELeiStavZadosti.HOTOVO || lei?.stavZadosti === ELeiStavZadosti.KONTROLA
    );

    const next = () => {
        if (lei?.loginless && currentStep === 0) setCurrentStep(2);
        else setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
    };

    const prev = () => {
        setCurrentStep(currentStep - 1);
        window.scrollTo(0, 0);
    };

    const queueNextStep = () => {
        lei?.idVarsymZadosti?.id && formData ? setNextStepQueued(true) : next();
    };

    useEffect(() => {
        setLoading(false);
        /* return () =>
            setLevel('lei', {
                selectedLei: lei?.selectedLei,
                ...(lei?.idVarsymZadosti?.varsym
                    ? {
                          idVarsymZadosti: {
                              varsym: lei?.idVarsymZadosti.varsym
                          }
                      }
                    : {})
            });*/
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getMatkasLei = () => {
        const filtereM = formData?.matky?.filter(
            e =>
                e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
        );
        return filtereM?.length ? filtereM[0].matkaslei : '';
    };

    const steps = [
        {
            title: '',
            content: <RozcestnikZalozeni nextStep={queueNextStep} setFormData={setFormData} />
        },
        {
            title: '',
            content: (
                <Pruvodce
                    prevStep={prev}
                    nextStep={queueNextStep}
                    pruvodceData={pruvodceData}
                    formDataSpolecnost={formData?.spolecnost}
                    typZadosti={formData?.typzadosti}
                    setPruvodceData={setPruvodceData}
                    setFormData={setFormData}
                />
            )
        },
        {
            title: 'LEI_AKTUALIZACE_INFORMACE_OPR',
            content: (
                <Osoba
                    prevStep={prev}
                    nextStep={queueNextStep}
                    setFormData={setFormData}
                    formDataOsoba={formData?.osoba}
                    pruvodceData={pruvodceData}
                    formDataSpolecnost={formData?.spolecnost}
                />
            )
        },
        {
            title: 'LEI_DETAIL_INFO_TITLE',
            content: (
                <DetailInfo
                    nextStep={queueNextStep}
                    prevStep={prev}
                    setFormData={setFormData}
                    formDataSpolecnost={formData?.spolecnost}
                    pruvodceData={pruvodceData}
                    entityCheck={formData?.entitycheck}
                    typZadosti={formData?.typzadosti}
                    primaMatkaLei={getMatkasLei()}
                    cisStaty={cisStaty}
                />
            )
        },
        {
            title: formData?.typzadosti === 'BRANCH' ? 'LEI_DETAIL_MAT_TITLE_BRANCH' : 'LEI_DETAIL_MAT_TITLE',
            content: (
                <PrimaMatka
                    nextStep={queueNextStep}
                    prevStep={prev}
                    typZadosti={formData?.typzadosti}
                    cisStaty={cisStaty}
                    setFormData={setFormData}
                    primaMatkaData={formData?.matky?.filter(
                        e =>
                            e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                            e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
                    )}
                    finalniMatkaData={formData?.matky?.filter(
                        e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                    )}
                    internationalMatka={formData?.matky?.filter(
                        e => e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
                    )}
                    icoSpolecnosti={formData?.spolecnost?.registrationauthorityentityid}
                />
            )
        },
        ...(formData?.typzadosti === 'FUND' || lei?.data?.typzadosti === 'FUND'
            ? [
                  {
                      title: 'LEI_FOND_TITLE',
                      content: (
                          <Profondy
                              nextStep={next}
                              prevStep={prev}
                              setFormData={setFormData}
                              vsechnyMatky={formData?.matky}
                          />
                      )
                  }
              ]
            : []),
        {
            title: 'PREHLED',
            content: (
                <Prehled
                    setShowTitle={setShowTitle}
                    prevStep={prev}
                    formData={formData}
                    idVarsymZadosti={lei?.idVarsymZadosti}
                    PrehledOnly={PrehledOnly}
                />
            )
        }
    ];

    useEffect(() => {
        if (PrehledOnly || currentStep === steps.length - 1) return;
        if (lei?.idVarsymZadosti?.id && formData && !formData.platba?.stavplatby) {
            setLoading(true);
            leiDataApi('put', lei.idVarsymZadosti.id, lei.idVarsymZadosti.varsym, formData)
                .then(() => {
                    if (nextStepQueued) {
                        next();
                        setNextStepQueued(false);
                    }
                })
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, lei?.idVarsymZadosti?.id]);

    useEffect(() => {
        if (!lei?.data?.typzadosti && !formData?.typzadosti) {
            setCurrentStep(0);
            lei?.data && setFormData(lei.data);
        } else if (lei && (currentStep !== steps.length - 1 || lei.loginless)) {
            lei?.data && setFormData(lei.data);
            !PrehledOnly && setCurrentStep(2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lei]);

    useEffect(() => {
        PrehledOnly && setCurrentStep(steps.length - 1);

        if (PrehledOnly) setCurrentStep(steps.length - 1);
        if (platbaLei) setCurrentStep(steps.length - 1);
        if (
            lei?.data?.platba?.stavplatby &&
            (lei.data.platba.stavplatby === EStavLeiPlatby.PLODLO ||
                lei.data.platba.stavplatby === EStavLeiPlatby.CEKA ||
                lei.data.platba.stavplatby === EStavLeiPlatby.VYPR)
        )
            setCurrentStep(steps.length - 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => setLevel('platbaLei', null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LoadingWrapper loading={loading}>
            <Title
                level={3}
                text={
                    currentStep > 0
                        ? currentStep === steps.length - 1 && PrehledOnly
                            ? `${getLanguageObject(language).LEI_PREHLED_ODESLANO_NOVY_TITLE} ${
                                  formData?.subcategory
                                      ? '– ' + getLanguageObject(language)[formData?.subcategory]
                                      : '– ' + getLanguageObject(language)['LEI_' + formData?.typzadosti]
                              } ${lei?.datumPodani ? '– ' + dayjs(lei?.datumPodani).format('DD.MM.YYYY') : ''}`
                            : `${getLanguageObject(language).LEI_ROZCESTNIK_TITLE} - ${
                                  getLanguageObject(language)['LEI_' + formData?.typzadosti] || ''
                              } ${
                                  formData?.typzadosti === ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY &&
                                  formData.subcategory
                                      ? '(' + getLanguageObject(language)[formData.subcategory] + ')'
                                      : ''
                              }`
                        : getLanguageObject(language).LEI_ROZCESTNIK_TITLE
                }
            />

            {showTitle && <Title level={4} text={steps[currentStep].title} />}
            <Row gutter={8}>
                <Col span={24}>{steps[currentStep].content}</Col>
            </Row>
        </LoadingWrapper>
    );
};

export default NovyLei;
